import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ActiveEarlyBird {
  id: string;
  name: string;
  campaign_type: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  end_date?: string;
  max_tickets?: number;
  tickets_sold: number;
  badge_label?: string;
  badge_color?: string;
  applies_to: string;
  product_ids: string[];
}

/**
 * Public-facing hook to fetch currently active early bird campaigns for an event.
 * Used by hero section, shop, and product cards to display promotions.
 */
export const useActiveEarlyBird = (eventId?: string) => {
  const [activeCampaigns, setActiveCampaigns] = useState<ActiveEarlyBird[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!eventId) return;

    const fetch = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('early_bird_campaigns' as any)
          .select('*')
          .eq('event_id', eventId)
          .eq('is_active', true)
          // Only campaigns that apply automatically (no code / affiliate link
          // needed) — this must mirror the server's auto-pricing so the price
          // shown in the shop is the price actually charged.
          .in('campaign_type', ['time_based', 'quantity_based'])
          .order('priority', { ascending: false });

        if (error) throw error;

        const now = new Date();
        const active = ((data || []) as any as ActiveEarlyBird[]).filter((c: any) => {
          // Not started yet
          if (c.start_date && new Date(c.start_date) > now) return false;
          // Ended
          if (c.end_date && new Date(c.end_date) < now) return false;
          // Sold out (quantity-based)
          if (c.max_tickets && c.tickets_sold >= c.max_tickets) return false;
          return true;
        });

        setActiveCampaigns(active);
      } catch (err) {
        console.error('Error fetching active early bird campaigns:', err);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [eventId]);

  // Main event tickets use category 'festival_pass'; treat all ticket-like
  // categories as "tickets" for tickets_only campaigns.
  const TICKET_CATEGORIES = ['ticket', 'tickets', 'ski_ticket', 'festival_pass', 'day_ticket'];

  /** Get the best campaign applicable to a specific product */
  const getBestCampaignForProduct = (productId: string, productCategory?: string): ActiveEarlyBird | null => {
    for (const c of activeCampaigns) {
      if (c.applies_to === 'all') return c;
      if (c.applies_to === 'tickets_only' && productCategory && TICKET_CATEGORIES.includes(productCategory)) return c;
      if (c.applies_to === 'specific' && c.product_ids?.includes(productId)) return c;
    }
    return null;
  };

  /** Get the top campaign for hero display */
  const topCampaign = activeCampaigns.length > 0 ? activeCampaigns[0] : null;

  /** Calculate discounted price */
  const getDiscountedPrice = (basePrice: number, campaign: ActiveEarlyBird): number => {
    if (campaign.discount_type === 'percentage') {
      return Math.max(0, basePrice - (basePrice * campaign.discount_value) / 100);
    }
    return Math.max(0, basePrice - campaign.discount_value);
  };

  return { activeCampaigns, topCampaign, loading, getBestCampaignForProduct, getDiscountedPrice };
};
